import React, { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFinancialPlan } from '../../context/FinancialPlanContext';
import { useEntitlement } from '../../context/EntitlementContext';
import { listPlans, canAddPlan, newPlanId, MAX_PLANS } from '../../utils/planLibrary';
import { ProTag } from './SectionNav';

const ACTION = 'min-h-[44px] px-3 rounded border border-surface-border text-xs font-mono uppercase tracking-wider text-txt-secondary hover:text-txt-primary hover:border-txt-secondary';

function PlanRow({ plan, onlyPlan, onOpen, onRename, onDelete }) {
  const [mode, setMode] = useState('view'); // 'view' | 'rename' | 'confirmDelete'
  const [name, setName] = useState(plan.name);

  const saveName = () => {
    onRename(plan.id, name);
    setMode('view');
  };

  return (
    <li className={`rounded border p-3 ${plan.active ? 'border-terminal-dark-green bg-terminal-dark-green/10' : 'border-surface-border'}`}>
      {mode === 'rename' ? (
        <form onSubmit={(e) => { e.preventDefault(); saveName(); }} className="flex flex-wrap gap-2">
          <input
            autoFocus
            aria-label="Plan name"
            className="terminal-input flex-1 min-w-0"
            value={name}
            maxLength={40}
            onChange={(e) => setName(e.target.value)}
          />
          <button type="submit" className={ACTION}>Save</button>
          <button type="button" className={ACTION} onClick={() => { setName(plan.name); setMode('view'); }}>Cancel</button>
        </form>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 mb-2">
            <span className="text-txt-primary text-sm font-mono break-words min-w-0">{plan.name}</span>
            {plan.active && <span className="flex-shrink-0 text-terminal-green text-xs font-mono uppercase tracking-wider">Open now</span>}
          </div>
          {mode === 'confirmDelete' ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-terminal-amber text-xs font-mono">Delete this plan and its inputs? This cannot be undone.</span>
              <button type="button" className={`${ACTION} border-terminal-red-dim text-terminal-red`} onClick={() => onDelete(plan.id)}>Delete</button>
              <button type="button" className={ACTION} onClick={() => setMode('view')}>Keep</button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {!plan.active && <button type="button" className={`${ACTION} border-terminal-dark-green text-terminal-green`} onClick={() => onOpen(plan.id)}>Open</button>}
              <button type="button" className={ACTION} onClick={() => setMode('rename')}>Rename</button>
              {!onlyPlan && <button type="button" className={ACTION} onClick={() => setMode('confirmDelete')}>Delete</button>}
            </div>
          )}
        </>
      )}
    </li>
  );
}

function PlansModal({ onClose }) {
  const { state, dispatch } = useFinancialPlan();
  const { isPro, openPaywall } = useEntitlement();
  const [newName, setNewName] = useState('');
  const titleId = useId();
  const plans = listPlans(state);
  const room = canAddPlan(state);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const add = (source) => {
    if (!isPro) {
      onClose();
      openPaywall('multiplePlans');
      return;
    }
    dispatch({ type: 'CREATE_PLAN', payload: { id: newPlanId(), name: newName, source } });
    setNewName('');
  };

  return createPortal(
    <div className="fixed inset-0 z-[115] flex items-end sm:items-center justify-center" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div className="absolute inset-0 bg-black/70" onPointerDown={onClose} />
      <div className="relative w-full sm:max-w-lg sm:mx-4 max-h-[88vh] flex flex-col bg-surface-elevated border border-terminal-cyan/40 rounded-t-xl sm:rounded-xl">
        <div className="flex items-start justify-between gap-3 p-5 pb-3 border-b border-surface-border">
          <div>
            <h2 id={titleId} className="font-display font-bold text-terminal-green uppercase tracking-wider text-base">Your Plans</h2>
            <p className="text-txt-secondary text-sm mt-1">
              Each plan keeps its own numbers. Opening one swaps it in; the others stay as you left them.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="flex-shrink-0 min-h-[44px] min-w-[44px] -mt-2 -mr-2 text-txt-secondary hover:text-txt-primary text-xl font-mono">&times;</button>
        </div>

        <div className="overflow-y-auto p-5 pt-4">
          <ul className="space-y-2">
            {plans.map((plan) => (
              <PlanRow
                key={plan.id}
                plan={plan}
                onlyPlan={plans.length === 1}
                onOpen={(id) => { dispatch({ type: 'SWITCH_PLAN', payload: id }); onClose(); }}
                onRename={(id, name) => dispatch({ type: 'RENAME_PLAN', payload: { id, name } })}
                onDelete={(id) => dispatch({ type: 'DELETE_PLAN', payload: id })}
              />
            ))}
          </ul>

          <div className="mt-5 pt-4 border-t border-surface-border">
            <h3 className="font-display font-bold text-terminal-amber uppercase tracking-wider text-xs mb-2 flex items-center gap-2">
              Add a Plan {!isPro && <ProTag />}
            </h3>
            {room ? (
              <>
                <label htmlFor="new-plan-name" className="block text-txt-secondary text-xs uppercase tracking-wider mb-1 font-mono">Name</label>
                <input
                  id="new-plan-name"
                  className="terminal-input w-full mb-3"
                  value={newName}
                  maxLength={40}
                  placeholder="For example: Retire at 58"
                  onChange={(e) => setNewName(e.target.value)}
                />
                <div className="flex flex-wrap gap-2">
                  <button type="button" className={`${ACTION} border-terminal-dark-green text-terminal-green`} onClick={() => add('copy')}>Copy the open plan</button>
                  <button type="button" className={ACTION} onClick={() => add('blank')}>Start blank</button>
                  <button type="button" className={ACTION} onClick={() => add('sample')}>Sample numbers</button>
                </div>
                <p className="text-txt-secondary text-xs font-mono mt-3">
                  {plans.length} of {MAX_PLANS} plans. A copy is the quickest way to test one change, like a different retirement age.
                </p>
              </>
            ) : (
              <p className="text-txt-secondary text-xs font-mono">You have {MAX_PLANS} plans, the most that can be kept. Delete one to add another.</p>
            )}
          </div>
        </div>

        <div className="p-5 pt-3 safe-bottom">
          <button type="button" onClick={onClose} className="w-full glow-btn glow-btn-green font-mono text-sm">Done</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/** Shows which plan is open and opens the list of plans. */
export default function PlanSwitcher({ className = '' }) {
  const { state } = useFinancialPlan();
  const [open, setOpen] = useState(false);
  const count = state.savedPlans.length + 1;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        className={`w-full flex items-center justify-between gap-2 min-h-[44px] px-3 rounded-lg border border-surface-border text-left hover:border-terminal-cyan/60 ${className}`}
      >
        <span className="min-w-0">
          <span className="block text-txt-muted text-[10px] font-mono uppercase tracking-wider leading-tight">Plan{count > 1 ? `, ${count} in all` : ''}</span>
          <span className="block text-txt-primary text-xs font-mono truncate">{state.planName}</span>
        </span>
        <span className="flex-shrink-0 text-terminal-cyan text-xs font-mono uppercase tracking-wider">Plans</span>
      </button>
      {open && <PlansModal onClose={() => setOpen(false)} />}
    </>
  );
}
